import { useEffect, useRef, useState } from "react";
import { Bell, X, ChevronRight, Clock, MapPin, Check, Gift, DollarSign, Loader2 } from "lucide-react";
import { staffMe } from "@/utils/staffAuth";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const accent = "#d4a574";

// Suite colour map — mirrors backend SUITES so the iPad banner picks the
// right room's accent without a network round-trip per render.
const SUITE_ACCENT = {
  crown:   "#C6A961",   // champagne gold — Karine
  emerald: "#10B981",   // emerald — Tanya
  pearl:   "#F4E4D7",   // pearl — facial aesthetics
  noir:    "#9CA3AF",   // soft graphite (noir uses light text on dark bg)
};
function suiteAccent(id) {
  return SUITE_ACCENT[id] || accent;
}

/**
 * Live admin banner that pops up whenever a client self-checks in.
 *
 * - Subscribes to /api/checkin/admin/stream (Server-Sent Events).
 * - Replays recent (last 4h) check-ins on mount via /api/checkin/admin/recent
 *   so a freshly-opened tab catches up.
 * - Shows each check-in as a stack of cards in the bottom-right (top on mobile).
 * - Plays a soft chime on each new event (HTMLAudioElement, mute-toggle handled
 *   by the browser's autoplay policy — first chime needs a user gesture).
 * - "Dismiss" marks the check-in as seen server-side (POST /admin/seen/{id})
 *   so other admin tabs also see it cleared.
 *
 * Drop this anywhere inside an authed admin page tree. It's safe to mount
 * multiple times — only ONE SSE connection per browser tab is recommended,
 * so put it on the AdminAuthGate-wrapped layout once.
 */
export default function LiveCheckinBanner() {
  const [items, setItems] = useState([]); // active (not yet dismissed)
  const [me, setMe] = useState(null); // logged-in staff — powers "YOUR CLIENT" targeting
  const meRef = useRef(null);
  const [, setNowTick] = useState(0); // forces re-render every 15s for live wait-time counters
  const esRef = useRef(null);
  const audioRef = useRef(null);
  // Iter 359 · Feb 13 2026 · Karine ask — send-to-reception checkout handoff
  const [checkoutFor, setCheckoutFor] = useState(null); // { checkin item }
  const [checkoutAmount, setCheckoutAmount] = useState("");
  const [checkoutAddon, setCheckoutAddon] = useState({ name: "", price: "" });
  const [checkoutNote, setCheckoutNote] = useState("");
  const [checkoutSending, setCheckoutSending] = useState(null);
  const [handoffDone, setHandoffDone] = useState({}); // checkin_id → true

  // Who am I? (cached in staffAuth — no extra network hit on every page)
  useEffect(() => {
    staffMe().then((u) => { setMe(u); meRef.current = u; }).catch(() => {});
  }, []);

  // Flash the browser tab title while clients are waiting, so an arrival is
  // visible even when staff have a different tab in front (they're logged in
  // all day but busy). Restores the original title once the queue clears.
  useEffect(() => {
    if (items.length === 0) return undefined;
    const original = document.title;
    let flip = false;
    const id = setInterval(() => {
      flip = !flip;
      document.title = flip
        ? `🔔 ${items.length} client${items.length > 1 ? "s" : ""} waiting!`
        : original;
    }, 1500);
    return () => {
      clearInterval(id);
      document.title = original;
    };
  }, [items.length]);

  // Gentle re-chime every 90s while check-ins remain unacknowledged —
  // Karine/Tanya asked for alerts they can't miss while treating clients.
  useEffect(() => {
    if (items.length === 0) return undefined;
    const id = setInterval(() => {
      try { audioRef.current?.(); } catch { /* noop */ }
    }, 90000);
    return () => clearInterval(id);
  }, [items.length]);

  // Live tick every 15s so "Marc has been waiting 4 min" stays accurate
  // without us refetching anything.
  useEffect(() => {
    if (items.length === 0) return undefined;
    const id = setInterval(() => setNowTick((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, [items.length]);

  // Gentle synthesised chime — soft two-note "ding" (very low volume so it
  // doesn't startle a clinic room; plays ONCE per arrival, never repeats).
  useEffect(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioRef.current = () => {
        try {
          [[660, 0], [880, 0.18]].forEach(([freq, delay]) => {
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.connect(g);
            g.connect(ctx.destination);
            o.frequency.value = freq;
            o.type = "sine";
            const t = ctx.currentTime + delay;
            g.gain.setValueAtTime(0.0001, t);
            g.gain.exponentialRampToValueAtTime(0.06, t + 0.03);
            g.gain.exponentialRampToValueAtTime(0.0001, t + 0.6);
            o.start(t);
            o.stop(t + 0.65);
          });
        } catch { /* noop */ }
      };
    } catch {
      audioRef.current = null;
    }
  }, []);

  // Replay recent check-ins on mount (so a refreshed tab catches up)
  // Defensive client-side privacy filter — even though the backend now
  // scopes /recent and /stream by caller identity (owners see all, non-
  // owners see only their own clients), we re-apply the same rule client-
  // side so a stale build or a future regression can't leak Karine's
  // clients onto Tanya's iPad.
  const isOwnerCached = ((meRef.current?.role || "").toLowerCase() === "owner");
  const myEmailCached = (meRef.current?.email || "").toLowerCase();
  const canSee = (row) => {
    if (isOwnerCached) return true;
    if (!myEmailCached) return false;
    return (row.provider_email || "").toLowerCase() === myEmailCached;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/checkin/admin/recent?hours=4`, { credentials: "include" });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        // Only show un-seen ones AND only those the current user is allowed
        // to see (privacy gate — Tanya never sees Karine's clients).
        // "On my way" rows are intent, not arrival — the Front Desk Board owns them.
        const active = (data.rows || []).filter((x) => !x.seen_by_staff_at && x.presence !== "on_my_way" && canSee(x));
        setItems(active);
      } catch { /* offline-safe */ }
    })();
    return () => { cancelled = true; };
  }, [me]);  // re-run once me loads so filter applies correctly

  // Subscribe to the SSE stream
  useEffect(() => {
    try {
      const es = new EventSource(`${API}/api/checkin/admin/stream`, { withCredentials: true });
      esRef.current = es;
      es.addEventListener("checkin", (e) => {
        try {
          const evt = JSON.parse(e.data);
          // Defensive: drop events that don't belong to this caller. (Backend
          // already filters; this is belt + suspenders.)
          if (!canSee(evt)) return;
          setItems((prev) => {
            if (prev.some((p) => p.id === evt.id)) return prev;
            return [
              {
                id: evt.id,
                client_name: evt.client_name,
                client_email: evt.client_email,
                service_name: evt.service_name,
                location: evt.location,
                start_at: evt.start_at,
                provider_name: evt.provider_name,
                provider_email: evt.provider_email,
                suite_id: evt.suite_id,
                suite_name: evt.suite_name,
                checked_in_at: evt.at,
                deep_link: evt.deep_link,
              },
              ...prev,
            ].slice(0, 8);
          });
          // Soft chime ONCE — and only for the provider whose client arrived
          const myEmail = (meRef.current?.email || "").toLowerCase();
          if (myEmail && (evt.provider_email || "").toLowerCase() === myEmail) {
            try { audioRef.current?.(); } catch { /* noop */ }
          }
        } catch { /* malformed event */ }
      });
      es.onerror = () => {
        // Browser auto-reconnects; nothing for us to do.
      };
      return () => {
        try { es.close(); } catch { /* noop */ }
      };
    } catch {
      return undefined;
    }
  }, []);

  async function dismiss(id) {
    setItems((prev) => prev.filter((p) => p.id !== id));
    try {
      await fetch(`${API}/api/checkin/admin/seen/${id}`, {
        method: "POST",
        credentials: "include",
      });
    } catch { /* fire-and-forget */ }
  }

  async function callIn(id) {
    try {
      const r = await fetch(`${API}/api/checkin/admin/call-in/${id}`, {
        method: "POST",
        credentials: "include",
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        // Most likely 403 if the staff session expired — surface a hint
        // eslint-disable-next-line no-alert
        alert(data?.detail || "Couldn't call client in — please refresh your sign-in.");
        return;
      }
      // Clear the card from the banner; the client portal will pick up the
      // "called_in" SSE event and show its own confirmation.
      setItems((prev) => prev.filter((p) => p.id !== id));
    } catch {
      // eslint-disable-next-line no-alert
      alert("Network error calling client in. Try again.");
    }
  }

  // Per-card transient "✓ Sent" / "Cooldown" feedback after Marc pings
  // the provider — non-blocking, auto-clears after 4s.
  const [pingState, setPingState] = useState({});

  async function pingProvider(id, providerName) {
    setPingState((s) => ({ ...s, [id]: { kind: "sending" } }));
    try {
      const r = await fetch(`${API}/api/checkin/admin/ping-provider/${id}`, {
        method: "POST",
        credentials: "include",
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setPingState((s) => ({
          ...s,
          [id]: { kind: "error", msg: data?.detail || "Couldn't ping" },
        }));
      } else if (data.throttled) {
        setPingState((s) => ({
          ...s,
          [id]: { kind: "throttled", msg: `Just pinged · wait ${data.seconds_remaining || 60}s` },
        }));
      } else if (data.already_called_in) {
        setPingState((s) => ({
          ...s,
          [id]: { kind: "info", msg: `${providerName || "Provider"} already called them in` },
        }));
      } else if (data.preview_env) {
        setPingState((s) => ({
          ...s,
          [id]: { kind: "sent", msg: `Preview · would have texted ${providerName || "provider"}` },
        }));
      } else {
        setPingState((s) => ({
          ...s,
          [id]: { kind: "sent", msg: `✓ Texted ${providerName || "provider"}` },
        }));
      }
    } catch {
      setPingState((s) => ({ ...s, [id]: { kind: "error", msg: "Network error" } }));
    }
    setTimeout(() => {
      setPingState((s) => {
        const next = { ...s };
        delete next[id];
        return next;
      });
    }, 4000);
  }

  // Iter 359 · Feb 13 2026 · Karine ask — send a client's line-items from
  // the treatment room to the reception iPad. Creates a Square Open Ticket
  // via /api/staff/session-checkout (mode=open_ticket), which then shows up
  // under "Open Tickets" on every Square POS device signed into this
  // merchant (including the Reception Stand). Receptionist opens it, client
  // taps the reader — done, no re-entering line items.
  function openCheckoutFor(it) {
    setCheckoutFor(it);
    setCheckoutAmount("");
    setCheckoutAddon({ name: "", price: "" });
    setCheckoutNote("");
  }
  function closeCheckout() {
    setCheckoutFor(null);
    setCheckoutSending(null);
  }
  async function sendToReception(mode = "open_ticket") {
    if (!checkoutFor) return;
    const priceCents = Math.round(parseFloat(checkoutAmount || "0") * 100);
    if (!priceCents || priceCents < 0) {
      toast.error("Enter the amount for the treatment");
      return;
    }
    const items = [{
      name: checkoutFor.service_name || "Treatment",
      price_cents: priceCents,
      qty: 1,
    }];
    const addonPrice = Math.round(parseFloat(checkoutAddon.price || "0") * 100);
    if (checkoutAddon.name && addonPrice > 0) {
      items.push({ name: checkoutAddon.name, price_cents: addonPrice, qty: 1 });
    }
    setCheckoutSending(mode);
    try {
      const r = await fetch(`${API}/api/staff/session-checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode,
          booking_id: checkoutFor.booking_id || "",
          client_email: checkoutFor.client_email || "",
          client_name: checkoutFor.client_name || "",
          line_items: items,
          note: checkoutNote || undefined,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      if (mode === "open_ticket") {
        toast.success("Sent to Reception ✓  Open Ticket ready in Square", { duration: 6000 });
        setHandoffDone((s) => ({ ...s, [checkoutFor.id]: true }));
        closeCheckout();
      } else {
        // "all" mode — open Quick-Pay in a new tab so Karine can tap the reader in her room
        const url = data.quick_pay_url;
        if (url) {
          try { window.open(url, "_blank", "noopener"); } catch { /* noop */ }
          toast.success("Payment link opened — tap the Square reader in your room", { duration: 6000 });
          setHandoffDone((s) => ({ ...s, [checkoutFor.id]: true }));
          closeCheckout();
        } else {
          toast.error("Payment link not returned — try Send to Reception instead.");
          setCheckoutSending(null);
        }
      }
    } catch (e) {
      toast.error("Couldn't create the sale. " + (e.message || "Try again."), { duration: 6000 });
      setCheckoutSending(null);
    }
  }


  function waitInfo(iso) {
    if (!iso) return { label: "just now", mins: 0, tone: "ok" };
    const dt = new Date(iso);
    const mins = Math.max(0, Math.floor((Date.now() - dt.getTime()) / 60000));
    let label;
    if (mins < 1) label = "just now";
    else if (mins === 1) label = "1 min waiting";
    else if (mins < 60) label = `${mins} min waiting`;
    else {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      label = m ? `${h}h ${m}m waiting` : `${h}h waiting`;
    }
    // Tone escalates so Karine spots a long-waiting client at a glance
    const tone = mins >= 15 ? "urgent" : mins >= 8 ? "warn" : "ok";
    return { label, mins, tone };
  }

  if (items.length === 0) return null;

  return (
    <div
      className="fixed z-[300] bottom-4 right-4 sm:bottom-6 sm:right-6 flex flex-col gap-2 max-w-[360px] w-[calc(100vw-2rem)] pointer-events-none"
      data-testid="live-checkin-banner"
    >
      {items.map((it) => {
        const w = waitInfo(it.checked_in_at);
        const isMine =
          me?.email && it.provider_email &&
          me.email.toLowerCase() === String(it.provider_email).toLowerCase();
        const toneColor =
          w.tone === "urgent" ? "#f87171" : w.tone === "warn" ? "#fbbf24" : `${accent}cc`;
        const tonePill =
          w.tone === "urgent" ? "rgba(248,113,113,0.25)" :
          w.tone === "warn" ? "rgba(251,191,36,0.22)" :
          `${accent}33`;
        const borderTone =
          w.tone === "urgent" ? "rgba(248,113,113,0.7)" :
          w.tone === "warn" ? "rgba(251,191,36,0.6)" :
          `${accent}66`;
        return (
        <div
          key={it.id}
          className="bg-stone-950/95 backdrop-blur border rounded-2xl shadow-2xl p-4 pointer-events-auto animate-in slide-in-from-right duration-300"
          style={{
            borderColor: borderTone,
            boxShadow: `0 20px 50px -10px ${toneColor}55, 0 0 0 1px ${borderTone}`,
          }}
          data-testid={`checkin-card-${it.id}`}
        >
          <div className="flex items-start gap-3">
            <div
              className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center animate-pulse"
              style={{ background: tonePill, color: toneColor }}
            >
              <Bell className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="text-[10px] uppercase tracking-[0.18em] font-bold"
                style={{ color: toneColor }}
                data-testid={`checkin-wait-${it.id}`}
              >
                {w.tone === "urgent" ? "⚠ " : ""}Client checked in · {w.label}
              </p>
              {isMine && (
                <span
                  className="inline-block mt-1 px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-[0.15em]"
                  style={{ background: "rgba(244,114,182,0.25)", color: "#f9a8d4", border: "1px solid rgba(244,114,182,0.5)" }}
                  data-testid={`checkin-yours-${it.id}`}
                >
                  ★ Your client
                </span>
              )}
              <p className="text-white text-base font-semibold mt-0.5 truncate">{it.client_name || it.client_email}</p>
              <div className="mt-1.5 space-y-0.5 text-xs text-stone-300">
                {it.service_name && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3 h-3 text-stone-500" />
                    <span>{it.service_name}</span>
                  </div>
                )}
                {it.location && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3 h-3 text-stone-500" />
                    <span>{it.location}</span>
                  </div>
                )}
                {it.provider_name && (
                  <div className="text-stone-500">With {it.provider_name}</div>
                )}
                {it.suite_name && (
                  <div
                    className="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] border"
                    style={{
                      background: `${suiteAccent(it.suite_id)}22`,
                      color: suiteAccent(it.suite_id),
                      borderColor: `${suiteAccent(it.suite_id)}66`,
                    }}
                    data-testid={`checkin-suite-${it.id}`}
                  >
                    {it.suite_name}
                  </div>
                )}
                {it.kiosk_redemption && (
                  <div className="flex items-center gap-1.5 text-emerald-300">
                    <Gift className="w-3 h-3" />
                    <span>
                      ${Number(it.kiosk_redemption.dollar_value || 0).toFixed(0)} credit redeemed at kiosk
                      {it.kiosk_redemption.credit_code ? ` · ${it.kiosk_redemption.credit_code}` : ""}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => callIn(it.id)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full text-[11px] font-extrabold uppercase tracking-wider transition-transform hover:scale-[1.02] active:scale-100"
                  style={{
                    background: "linear-gradient(135deg, #10b981, #059669)",
                    color: "#fff",
                    boxShadow: "0 4px 14px rgba(16,185,129,0.35)",
                  }}
                  data-testid={`checkin-call-in-${it.id}`}
                  title="Send SMS: '[Your name] is ready — please come to Room [N]'"
                >
                  <Check className="w-3.5 h-3.5" /> I&apos;m Ready — Call In
                </button>
                <a
                  href={it.deep_link || `/admin/clients?q=${encodeURIComponent(it.client_email || "")}&autoselect=1`}
                  target={it.deep_link ? "_blank" : undefined}
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-transform hover:scale-[1.02]"
                  style={{ background: `${accent}22`, color: accent, border: `1px solid ${accent}66` }}
                  data-testid={`checkin-open-${it.id}`}
                >
                  Open <ChevronRight className="w-3 h-3" />
                </a>
              </div>
              {/* Ping Provider — front desk (owners only) can re-alert the
                  assigned provider via SMS if they're busy with another
                  client. Hidden on the provider's own card to keep the UI
                  clean (they don't need to ping themselves). */}
              {isOwnerCached && !isMine && it.provider_name && (
                <button
                  onClick={() => pingProvider(it.id, it.provider_name)}
                  disabled={pingState[it.id]?.kind === "sending"}
                  className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] transition-transform hover:scale-[1.02] active:scale-100 disabled:opacity-60"
                  style={{
                    background: pingState[it.id]?.kind === "sent"
                      ? "rgba(16,185,129,0.18)"
                      : pingState[it.id]?.kind === "throttled" || pingState[it.id]?.kind === "info"
                        ? "rgba(251,191,36,0.18)"
                        : pingState[it.id]?.kind === "error"
                          ? "rgba(248,113,113,0.18)"
                          : "rgba(96,165,250,0.18)",
                    color: pingState[it.id]?.kind === "sent"
                      ? "#6ee7b7"
                      : pingState[it.id]?.kind === "throttled" || pingState[it.id]?.kind === "info"
                        ? "#fde68a"
                        : pingState[it.id]?.kind === "error"
                          ? "#fca5a5"
                          : "#93c5fd",
                    border: "1px solid currentColor",
                  }}
                  data-testid={`checkin-ping-provider-${it.id}`}
                  title={`SMS ${it.provider_name} that ${it.client_name || "their client"} is in the waiting room`}
                >
                  {pingState[it.id]?.kind === "sending"
                    ? "Pinging…"
                    : pingState[it.id]?.msg
                      || `📲 Ping ${it.provider_name}`}
                </button>
              )}
              <button
                onClick={() => dismiss(it.id)}
                className="mt-1.5 text-stone-500 hover:text-white text-[10px] underline-offset-2 hover:underline w-full text-center"
                data-testid={`checkin-dismiss-${it.id}`}
              >
                Dismiss without calling
              </button>

              {/* Iter 359 · Send to Reception → Square Open Ticket */}
              <button
                onClick={() => openCheckoutFor(it)}
                disabled={handoffDone[it.id]}
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-extrabold uppercase tracking-[0.15em] transition-transform hover:scale-[1.02] active:scale-100 disabled:opacity-60"
                style={{
                  background: handoffDone[it.id] ? "rgba(16,185,129,0.18)" : "rgba(251,191,36,0.15)",
                  color: handoffDone[it.id] ? "#6ee7b7" : "#fcd34d",
                  border: `1px solid ${handoffDone[it.id] ? "rgba(16,185,129,0.6)" : "rgba(251,191,36,0.5)"}`,
                }}
                data-testid={`checkin-send-checkout-${it.id}`}
                title="Create a Square Open Ticket for this client and hand them off to Reception for payment"
              >
                {handoffDone[it.id] ? (
                  <><Check className="w-3 h-3" /> Sent to Reception</>
                ) : (
                  <><DollarSign className="w-3 h-3" /> Send to Reception → Checkout</>
                )}
              </button>
            </div>
            <button
              onClick={() => dismiss(it.id)}
              className="text-stone-600 hover:text-stone-300 flex-shrink-0"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        );
      })}

      {/* Iter 359 · Reception checkout modal — compact, focused UX */}
      {checkoutFor && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={closeCheckout}
          data-testid="checkout-modal"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-zinc-900 border border-amber-500/40 p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-400 flex items-center gap-1.5">
                  <DollarSign className="w-3.5 h-3.5" /> Send to Reception · Checkout
                </div>
                <div className="text-white text-lg font-bold mt-1">{checkoutFor.client_name || checkoutFor.client_email}</div>
                <div className="text-zinc-400 text-xs">{checkoutFor.service_name} · {checkoutFor.location}</div>
              </div>
              <button onClick={closeCheckout} className="text-zinc-500 hover:text-white" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                  Treatment amount (CAD)
                </label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    autoFocus
                    value={checkoutAmount}
                    onChange={(e) => setCheckoutAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-zinc-950 border border-zinc-700 rounded-lg pl-7 pr-3 py-2.5 text-white text-lg font-bold focus:border-amber-500 outline-none"
                    data-testid="checkout-amount-input"
                  />
                </div>
              </div>

              <details className="rounded-lg bg-zinc-950/60 border border-zinc-800">
                <summary className="px-3 py-2 text-[11px] uppercase tracking-widest text-zinc-400 font-bold cursor-pointer hover:text-white">
                  + Add-on / Extra line
                </summary>
                <div className="p-3 space-y-2">
                  <input
                    type="text"
                    value={checkoutAddon.name}
                    onChange={(e) => setCheckoutAddon((a) => ({ ...a, name: e.target.value }))}
                    placeholder="e.g. Diamond Tip Dermabrasion"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-white text-sm"
                    data-testid="checkout-addon-name"
                  />
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={checkoutAddon.price}
                      onChange={(e) => setCheckoutAddon((a) => ({ ...a, price: e.target.value }))}
                      placeholder="0.00"
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-md pl-6 pr-3 py-2 text-white text-sm"
                      data-testid="checkout-addon-price"
                    />
                  </div>
                </div>
              </details>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                  Note for reception (optional)
                </label>
                <input
                  type="text"
                  value={checkoutNote}
                  onChange={(e) => setCheckoutNote(e.target.value)}
                  placeholder="e.g. book follow-up in 4 weeks"
                  className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 mt-1 text-white text-sm"
                  data-testid="checkout-note"
                />
              </div>

              <div className="text-[11px] text-amber-200/70 bg-amber-950/30 border border-amber-500/20 rounded-md px-3 py-2">
                Pick where to close this sale — HST is calculated automatically either way.
              </div>

              <div className="grid grid-cols-1 gap-2">
                {/* Charge here — Quick-Pay link opens on your device */}
                <button
                  onClick={() => sendToReception("all")}
                  disabled={!!checkoutSending || !checkoutAmount}
                  className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-sm font-extrabold uppercase tracking-widest text-white transition-transform hover:scale-[1.02] active:scale-100 disabled:opacity-50"
                  style={{
                    background: "linear-gradient(135deg, #10b981, #059669)",
                    boxShadow: "0 6px 20px rgba(16,185,129,0.35)",
                  }}
                  data-testid="checkout-charge-here-btn"
                  title="Opens a Square Quick-Pay link on this device so you can tap the reader in your room"
                >
                  {checkoutSending === "all" ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Preparing…</>
                  ) : (
                    <><Check className="w-4 h-4" /> Charge in this Room</>
                  )}
                </button>

                {/* Send to Reception — Open Ticket on Reception Stand */}
                <button
                  onClick={() => sendToReception("open_ticket")}
                  disabled={!!checkoutSending || !checkoutAmount}
                  className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-sm font-extrabold uppercase tracking-widest text-white transition-transform hover:scale-[1.02] active:scale-100 disabled:opacity-50"
                  style={{
                    background: "linear-gradient(135deg, #f59e0b, #ea580c)",
                    boxShadow: "0 6px 20px rgba(245,158,11,0.35)",
                  }}
                  data-testid="checkout-send-btn"
                  title="Creates a Square Open Ticket that appears on the Reception Stand instantly"
                >
                  {checkoutSending === "open_ticket" ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                  ) : (
                    <><DollarSign className="w-4 h-4" /> Send to Reception</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
